type StreamWorkerProgress = {
  loaded: number
  total: number
}

type StreamDownloadOptions = {
  url: string
  timeoutMs: number
  credentials?: RequestCredentials
  onProgress?: (progress: StreamWorkerProgress) => void
}

type StreamDownloadJob = {
  promise: Promise<Blob>
  cancel: () => void
}

const DEFAULT_CONTENT_TYPE = 'application/octet-stream'

function canUseWorker() {
  return typeof Worker !== 'undefined' && typeof Blob !== 'undefined'
}

function createWorkerInstance() {
  return new Worker(
    new URL('../../workers/streamDownload.worker.js', import.meta.url),
    {
      type: 'module',
    }
  )
}

export function supportsStreamDownloadWorker() {
  return canUseWorker()
}

export function startStreamDownloadInWorker(
  options: StreamDownloadOptions
): StreamDownloadJob {
  if (!canUseWorker()) {
    return {
      promise: Promise.reject(new Error('worker is not supported')),
      cancel: () => undefined,
    }
  }

  const worker = createWorkerInstance()
  const jobId = `${Date.now()}-${Math.random().toString(16).slice(2)}`

  let done = false
  let timer: number | null = null

  const cleanup = () => {
    if (done) return
    done = true

    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }

    worker.onmessage = null
    worker.onerror = null
    worker.terminate()
  }

  const promise = new Promise<Blob>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent) => {
      const data = event?.data || {}
      if (data.jobId !== jobId) return

      if (data.type === 'progress') {
        if (typeof options.onProgress === 'function') {
          options.onProgress({
            loaded: Number(data.loaded) || 0,
            total: Number(data.total) || 0,
          })
        }
        return
      }

      if (data.type === 'done') {
        cleanup()
        const contentType = data.contentType || DEFAULT_CONTENT_TYPE
        resolve(new Blob([data.buffer], { type: contentType }))
        return
      }

      if (data.type === 'aborted') {
        cleanup()
        reject(new DOMException('aborted', 'AbortError'))
        return
      }

      if (data.type === 'error') {
        cleanup()
        reject(new Error(data.message || 'worker stream download failed'))
      }
    }

    worker.onerror = (error) => {
      cleanup()
      reject(new Error(error?.message || 'worker execution failed'))
    }

    timer = window.setTimeout(() => {
      try {
        worker.postMessage({
          type: 'abort',
          jobId,
        })
      } catch {
        // ignore post abort errors
      }

      cleanup()
      reject(new Error('worker download timeout'))
    }, options.timeoutMs)

    worker.postMessage({
      type: 'start',
      jobId,
      url: options.url,
      credentials: options.credentials || 'include',
    })
  })

  const cancel = () => {
    if (done) return

    try {
      worker.postMessage({
        type: 'abort',
        jobId,
      })
    } catch {
      // ignore post abort errors
    }

    cleanup()
  }

  return {
    promise,
    cancel,
  }
}
